/**
 * MetricChart - Interactive chart for a single metric with signal markers
 */

import { useMemo } from 'react';
import {
  ResponsiveContainer,
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ReferenceDot,
  CartesianGrid,
  Legend,
} from 'recharts';
import type { MetricSeries, Signal } from './types';
import { 
  formatAmountWithDisplayFormat, 
  determineOptimalUnit, 
  type DisplayUnit,
  type DisplayFormat,
} from '../utils';

interface MetricChartProps {
  metric: MetricSeries;
  signals: Signal[];
  selectedPeriod: string | null;
  onPeriodSelect: (period: string | null) => void;
  showComparative?: boolean;
  displayFormat?: DisplayFormat;
}

export function MetricChart({ 
  metric, 
  signals, 
  selectedPeriod, 
  onPeriodSelect,
  showComparative = true,
  displayFormat = 'auto',
}: MetricChartProps) {
  // Determine optimal display unit for consistent formatting
  const autoUnit: DisplayUnit = useMemo(() => {
    const allValues = [...metric.values, ...(metric.comparative_values || [])];
    return determineOptimalUnit(allValues);
  }, [metric]);

  // Helper function for formatting
  const fmt = (value: number) => formatAmountWithDisplayFormat(value, displayFormat, autoUnit);

  const chartData = useMemo(() => {
    return metric.periods.map((period, idx) => {
      const periodSignals = signals.filter(s => s.period === period);
      const highestSeverity = periodSignals.reduce<string | null>((acc, s) => {
        if (s.severity === 'HIGH') return 'HIGH';
        if (s.severity === 'MEDIUM' && acc !== 'HIGH') return 'MEDIUM';
        if (s.severity === 'LOW' && !acc) return 'LOW';
        return acc;
      }, null);
      
      return {
        period,
        periodLabel: formatPeriodLabel(period),
        value: metric.values[idx],
        comparative: metric.comparative_values?.[idx],
        hasSignal: periodSignals.length > 0,
        signalSeverity: highestSeverity,
        signalCount: periodSignals.length,
        isSelected: period === selectedPeriod,
      };
    });
  }, [metric, signals, selectedPeriod]);
  
  const signalMarkers = useMemo(() => {
    return chartData.filter(d => d.hasSignal);
  }, [chartData]);
  
  const signalColor = (severity?: string | null) => {
    switch (severity) {
      case 'HIGH': return 'hsl(var(--destructive))';
      case 'MEDIUM': return 'hsl(var(--warning, 38 92% 50%))';
      default: return 'hsl(var(--primary))';
    }
  };
  
  const handleChartClick = (data: any) => {
    if (data?.activePayload?.[0]?.payload?.period) {
      const period = data.activePayload[0].payload.period;
      onPeriodSelect(period === selectedPeriod ? null : period);
    }
  };
  
  return (
    <ResponsiveContainer width="100%" height={320}>
      <ComposedChart
        data={chartData}
        onClick={handleChartClick}
        margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
      >
        <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
        <XAxis 
          dataKey="periodLabel" 
          tick={{ fontSize: 12 }}
          className="fill-muted-foreground"
        />
        <YAxis 
          tickFormatter={fmt}
          tick={{ fontSize: 12 }}
          className="fill-muted-foreground"
          width={80}
        />
        <Tooltip
          content={({ active, payload }) => {
            if (!active || !payload?.length) return null;
            const data = payload[0].payload;
            return (
              <div className="bg-background border rounded-lg p-3 shadow-lg">
                <p className="font-medium text-sm">{data.period}</p>
                <p className="text-sm mt-1">
                  <span className="text-muted-foreground">Värde: </span>
                  <span className="font-medium">{fmt(data.value)}</span>
                </p>
                {data.comparative !== undefined && data.comparative !== 0 && (
                  <p className="text-sm text-muted-foreground">
                    Föregående år: {fmt(data.comparative)}
                  </p>
                )}
                {data.hasSignal && (
                  <p className="text-sm mt-2 text-destructive flex items-center gap-1">
                    ⚠ {data.signalCount} signal{data.signalCount > 1 ? 'er' : ''}
                  </p>
                )}
              </div>
            );
          }}
        />
        
        {showComparative && metric.comparative_values && (
          <Legend 
            verticalAlign="top" 
            height={36}
            formatter={(value) => (
              <span className="text-sm text-muted-foreground">{value}</span>
            )}
          />
        )}
        
        {/* Bars for current year */}
        <Bar
          dataKey="value"
          name="Innevarande år"
          fill="hsl(var(--primary))"
          opacity={0.85}
          radius={[4, 4, 0, 0]}
          cursor="pointer"
        />
        
        {/* Line for previous year comparison */}
        {showComparative && metric.comparative_values && (
          <Line
            type="monotone"
            dataKey="comparative"
            name="Föregående år"
            stroke="hsl(var(--muted-foreground))"
            strokeWidth={2}
            strokeDasharray="5 5"
            dot={false}
          />
        )}
        
        {/* Signal markers */}
        {signalMarkers.map((marker, idx) => (
          <ReferenceDot
            key={`signal-${idx}`}
            x={marker.periodLabel}
            y={marker.value}
            r={8}
            fill={signalColor(marker.signalSeverity)}
            stroke="hsl(var(--background))"
            strokeWidth={2}
          />
        ))}
      </ComposedChart>
    </ResponsiveContainer>
  );
}

/**
 * Format period (YYYY-MM) to short label
 */
function formatPeriodLabel(period: string): string {
  const [year, month] = period.split('-');
  const monthNames: Record<string, string> = {
    '01': 'Jan', '02': 'Feb', '03': 'Mar', '04': 'Apr',
    '05': 'Maj', '06': 'Jun', '07': 'Jul', '08': 'Aug',
    '09': 'Sep', '10': 'Okt', '11': 'Nov', '12': 'Dec',
  };
  return `${monthNames[month] || month} ${year.slice(2)}`;
}
